import { useSnackbar } from 'notistack';
import React from 'react';

import { Toast } from 'src/components/Toast';
import { RecommendedResource } from 'src/constants';
import { useUpdateResourcesDismissalMutation } from 'src/services/api';
import { AlertVariant } from 'src/store/ui/types';

import { HomePageSection } from '../HomePageSection';

import { ResourceCard } from './ResourceCard';

interface ResourcesSectionProps {
  resources: Array<RecommendedResource>;
}
export const ResourcesSection: React.FC<ResourcesSectionProps> = ({
  resources,
}) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [updateResourcesDismissal] = useUpdateResourcesDismissalMutation();

  const loadedResources = React.useMemo(
    () =>
      resources.map(({ content, CardUUID, visibility }) => ({
        id: CardUUID,
        title: content.title,
        description: content.body,
        primaryActionLabel: content.button_primary,
        secondaryActionLabel: content.button_secondary,
        thumbnail: content.thumbnail,
        asset: content.asset,
        primaryActionLink: content.action_primary,
        secondaryActionLink: content.action_secondary,
        hideOnMobile: visibility.hide_on_mobile,
      })),
    [resources],
  );

  /**
   * When undo snackbar action is clicked, this function will be called.
   * It will update the dismissal status of the resource to 'not dismissed'.
   */
  const handleUndoDismiss = React.useCallback((dismissedResourceId: string) => {
    updateResourcesDismissal({
      structFields: {
        dismissed: { [dismissedResourceId]: false },
      },
    });
    // hide snackbar right away
    closeSnackbar(dismissedResourceId);
  }, []);

  // this defines the snackbar that will be displayed when a resource is dismissed
  const successDismissSnackbar = React.useCallback(
    (snackbarKey: string | number, dismissedResourceId: string) => (
      <div key={snackbarKey}>
        <Toast
          variant={AlertVariant.SUCCESS}
          onClose={() => closeSnackbar(String(snackbarKey))}
          description="Card dismissed"
          actionProps={{
            onClick: () => handleUndoDismiss(dismissedResourceId),
            children: 'Undo',
            className: 'text-base',
          }}
        />
      </div>
    ),
    [closeSnackbar],
  );

  /**
   * When dismiss resource button is clicked, this function will be called.
   * It updates the resources dismissal state by calling the updateResourcesDismissal mutation.
   * It also displays a snackbar to notify the user that the resource has been dismissed.
   * @param dismissedResourceCardId
   */
  const handleDismissResource = async (dismissedResourceCardId: string) => {
    const stagedResourcesDismissal = resources.reduce(
      (dismissalsMap: Record<string, boolean>, { CardUUID: resourceId }) => {
        dismissalsMap[resourceId] = resourceId === dismissedResourceCardId;
        return dismissalsMap;
      },
      {},
    );

    try {
      const result = await updateResourcesDismissal({
        structFields: {
          dismissed: stagedResourcesDismissal,
        },
      });
      if (result.data) {
        enqueueSnackbar('Resource has been dismissed', {
          key: dismissedResourceCardId,
          content: (snackbarKey: string | number) =>
            successDismissSnackbar(snackbarKey, dismissedResourceCardId),
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <HomePageSection title="Recommended resources">
      {loadedResources.map((resource) => (
        <ResourceCard
          key={resource.title}
          onDismissClick={() => handleDismissResource(resource.id)}
          {...resource}
        />
      ))}
    </HomePageSection>
  );
};
